import {
	ColumnsProps,
	ScrollType,
	buildActionColumn,
	buildColumn,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getUniqueValuesArray,
	handleResponse,
	invertSelectedRowKeys,
	lockedValidation,
	useHandleOnChangeTableLogic,
} from '@/Helper';
import { deviceAdminApi } from '@/api/DeviceAdminApi';
import { ButtonDropdown, HelpFactoryComponent, ModalConfirmationList, SearchColumn } from '@/components/common';
import { EditableCell } from '@/components/common/EditableCell/EditableCell';
import { SecuredComponents, User, getPermissionErrorMessage } from '@/model/AccountModel';
import {
	BaseColumns,
	PaginationSetting,
	ResponseObject,
	ResponseStatusCode,
	RolesColumn,
	SelectOptions,
	SelectedDeviceKeys,
	SorterConfigEx,
} from '@/model/CommonModel';
import { ButtonBuilder, CurrentDeviceControlObj, DeviceObjectType, OptionsButtonBuilder, UserRole, VelocityConfigurationModel } from '@/model/DeviceAdminModel';
import { HelpFeature } from '@/model/HelpModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setCurrentDevice } from '@/store/common/actions';
import { selectCurrentDevice } from '@/store/common/selectors';
import {
	clearVelocityConfigurationSelectionsAction,
	setRolesBy,
	setRolesByData,
	setVelocityConfigurationEditMode,
	setVelocityConfigurationFilterMode,
	setVelocitySelectedRowKeysPaginationAction,
} from '@/store/deviceControl/actions';
import { selectTablePaginationSetting, selectVelocityConfiguration } from '@/store/deviceControl/selectors';
import { CaretDownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Space, Table, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import React, { useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { AddRolesModal } from './AddRolesModal/AddRolesModal';
import styles from './roles.module.scss';

type Props = {};

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const componentPermission = User.getComponentPermission(user, SecuredComponents.Roles);
const isUserAdmin: boolean = user.isAdmin;
const scroll: ScrollType = { x: 379, y: 430 };
const maxLength: number = 20;
const deviceObjectType: DeviceObjectType = DeviceObjectType.Roles;
const topicId: string = 'Roles';

const createButtonOptions = (isMoreThanOneSelected: boolean, isAdminRoleSelected: boolean): OptionsButtonBuilder<string> => {
	return {
		labelOrIcon: '...',
		options: [
			{
				label: _('Copy'),
				disabled: !isUserAdmin || isMoreThanOneSelected,
				value: 'copy',
				title: getPermissionErrorMessage(isUserAdmin),
			},
			{
				label: _('Edit'),
				disabled: !isUserAdmin || isMoreThanOneSelected,
				value: 'edit',
				title: getPermissionErrorMessage(isUserAdmin, false, isMoreThanOneSelected),
			},
			{
				label: _('Rename'),
				disabled: !isUserAdmin || isMoreThanOneSelected || isAdminRoleSelected,
				value: 'rename',
				title: getPermissionErrorMessage(isUserAdmin, false, isMoreThanOneSelected),
			},
			{
				label: _('Delete'),
				disabled: !isUserAdmin || (!isMoreThanOneSelected && isAdminRoleSelected),
				value: 'delete',
				title: getPermissionErrorMessage(isUserAdmin),
			},
		],
	};
};

const Roles: React.FC<Props> = () => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();

	const [isItemOnSamePage, setIsItemOnSamePage] = useState<boolean>(false);
	const [isCopyMode, setIsCopyMode] = useState<boolean>(false);
	const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
	const [form] = Form.useForm();
	const [editingKey, setEditingKey] = useState('');
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [isTableLoading, setIsTableLoading] = useState<boolean>(false);
	const [tableData, setTableData] = useState<RolesColumn[]>([]);
	const [sorterConfigEx, setSorterConfigEx] = useState<SorterConfigEx>({ columnKey: undefined, order: undefined });

	const isSearchPerformed = useRef<boolean>(false);
	const redirectPage = useRef<boolean>(false);

	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	const { isEditMode, roles, isFilterMode, selectedRowKeysPagination }: VelocityConfigurationModel = useStoreSelector(selectVelocityConfiguration);
	const tablePaginationSetting: PaginationSetting = useStoreSelector<PaginationSetting>(selectTablePaginationSetting);
	const currentDevice: CurrentDeviceControlObj = useStoreSelector<CurrentDeviceControlObj>(selectCurrentDevice);
	//#endregion

	useEffect(() => {
		return () => {
			clearSelection();
		};
	}, []);

	useEffect(() => {
		const mapRes: RolesColumn[] = createData();
		setTableData(mapRes);
	}, [JSON.stringify(roles)]);

	useEffect(() => {
		if (currentDevice.Address === 'passback') {
			setSelectedRowKeys([currentDevice.Id.toString()]);
			setCurrentDevice({
				...currentDevice,
				Address: '',
			});
		}
	}, [JSON.stringify(currentDevice)]);

	const clearSelection = () => {
		dispatch(clearVelocityConfigurationSelectionsAction({ objectType: deviceObjectType }));
		setSelectedRowKeys([]);
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		const isSamePage: boolean = selectedRowKeysPagination[0]?.PaginationPage === page;
		setIsItemOnSamePage(isSamePage);
		if (pageSize !== tablePaginationSetting.PageSize) {
			clearSelection();
		}
	};

	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		handleChangePagination,
		tablePaginationSetting.PageNumber,
		tablePaginationSetting.PageSize,
		tablePaginationSetting.TotalItems,
		undefined,
		selectedRowKeys
	);

	const handleOnRoleModalOpen = (newRoleId: number, isNewRole: boolean) => {
		const newDevice: CurrentDeviceControlObj = {
			...currentDevice,
			Id: newRoleId,
			Address: '',
			IsModalOpen: true,
			DeviceObjectType: DeviceObjectType.Roles,
			IsNew: isNewRole,
		};
		dispatch(setCurrentDevice(newDevice));
	};

	const onRemoveRoleLocks = async (role: UserRole): Promise<void> => {
		await deviceAdminApi.unlockComponentDeviceAdmin(role.Id, SecuredComponents.Roles);
	};

	const onDeleteRoleRequest = async (role: UserRole): Promise<void> => {
		const responseOrphan = await deviceAdminApi.getOrphanedOperatorsWhenDeletingRole(role.Id);
		if (handleResponse(responseOrphan)) {
			return;
		}

		if (responseOrphan.Entity.length) {
			ModalConfirmationList({
				width: '600px',
				data: responseOrphan.Entity.map((name: string) => ({ Name: name })),
				onCancel: () => onRemoveRoleLocks(role),
				onConfirm: () => deleteRoleRequest(role).then(() => dispatch(setRolesBy(tablePaginationSetting))),
				customContent: (
					<div>
						<p>{`The following operator(s) will be orphaned if the role '${role.Name}' gets deleted and will have no access to Velocity.`}</p>
						<p>{`Do you want to continue?`}</p>
					</div>
				),
			});
		} else {
			deleteRoleRequest(role);
		}
	};

	const deleteRoleRequest = async (role: UserRole): Promise<void> => {
		return deviceAdminApi
			.deleteRole(role.Id, role.Name)
			.then(response => {
				handleResponse(response);
			})
			.finally(() => {
				return onRemoveRoleLocks(role);
			});
	};

	const onDelete = async (selectedRoles: UserRole[]): Promise<void> => {
		for (const role of selectedRoles) {
			await onDeleteRoleRequest(role);
		}
	};

	const setRedirectPage = (value: boolean) => {
		redirectPage.current = value;
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const disabled: boolean = editingKey !== '';
	const isMoreThanOneSelected: boolean = selectedRowKeys.length > 1;
	const isAdminRoleSelected: boolean = selectedRowKeys.find(key => Number(key) === 1) !== undefined;
	const items = createButtonOptions(isMoreThanOneSelected, isAdminRoleSelected);

	const handleOnSearchResults = (searchedValue: string) => {
		dispatch(setRolesBy({ ...tablePaginationSetting, TotalItems: 0, PageNumber: 1, SearchedValue: searchedValue }));
	};

	const columns: ColumnsProps<RolesColumn>[] = [
		{
			...buildColumn(_('Name'), 'Name', '220px', 'start'),
			key: 'Name',
			editable: true,
			sorter: !isEditMode,
			sortOrder: sorterConfigEx.columnKey === 'Name' && sorterConfigEx.order,
			...SearchColumn({
				maxLength,
				dataIndex: 'Name',
				reset: undefined,
				label: undefined,
				notVisible: isEditMode,
				resetSearch: shouldResetSearchColumn,
				setIsFilterMode: setVelocityConfigurationFilterMode,
				clearSelection,
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => handleOnSearchResults(searchedValue),
				setSearchedValue: undefined,
				searchColumnId: undefined,
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			}),
		},
		{
			...buildColumn(_('Description'), 'Description', 'auto', 'start'),
			editable: false,
			sorter: (a, b) => a.Description.localeCompare(b.Description),
		},
	];

	if (isEditMode) {
		columns.unshift(buildActionColumn(disabled));
	}

	const onChangeSelection = (key: BaseColumns) => {
		const id = key.key.toString();
		if (selectedRowKeys.findIndex(key => key === id) === -1) {
			const cloneState: UserRole[] = roles.map(x => (x.Id.toString() === id ? { ...x, checked: true } : { ...x, checked: false }));
			batch(() => {
				setIsItemOnSamePage(true);
				dispatch(setRolesByData(cloneState));
				setSelectedRowKeys([id]);
			});
		}
	};

	const contextMenuOptions: SelectOptions<string>[] = [...items?.options];
	const isEditing = (record: RolesColumn) => record.key.toString() === editingKey;

	const mergedColumns = columns.map(col =>
		!col.editable
			? {
					...col,
					onCell: (record: RolesColumn) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
					}),
			  }
			: {
					...col,
					onCell: (record: RolesColumn) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
						inputType: 'text',
						editing: isEditing(record),
					}),
			  }
	);

	const getSelectedRoles = async (): Promise<SelectedDeviceKeys[]> => {
		const response = await deviceAdminApi.getVelocityDevicesBySelectedKeys(deviceObjectType, selectedRowKeys as string[]);
		if (handleResponse(response)) {
			return [];
		}

		return response.Entity;
	};

	const handleActionScope = async (key: string) => {
		const selectedRolesKeys = await getSelectedRoles();
		const selectedRoles = selectedRolesKeys.map(x => ({ Id: x.Id, Name: x.Name } as UserRole));
		switch (key) {
			case 'copy': {
				const isLocked: boolean = await lockedValidation(deviceObjectType, selectedRoles[0].Id, false);
				if (isLocked) {
					return;
				}
				batch(() => {
					setIsAddModalOpen(true);
					setIsCopyMode(true);
				});
				break;
			}
			case 'delete': {
				const operatorAction: string = _('Delete');
				const selectedRolesFiltered = selectedRoles.filter(sr => sr.Name !== 'Administrators');
				ModalConfirmationList({
					type: operatorAction,
					translationKey: 'AreYouSureYouWantToTheseRoles',
					data: selectedRolesFiltered,
					onConfirm: () =>
						onDelete(selectedRolesFiltered).then(() => {
							batch(() => {
								setSelectedRowKeys([]);
								dispatch(setVelocitySelectedRowKeysPaginationAction([]));
								dispatch(setRolesBy(tablePaginationSetting));
							});
						}),
				});
				break;
			}
			case 'rename': {
				const { Id } = selectedRoles[0];
				dispatch(setVelocityConfigurationEditMode(true));
				const findKey: RolesColumn = tableData.find(row => row.key.toString() === Id.toString());
				edit(findKey);
				const cloneState: UserRole[] = changeStateProps(Id, { editable: true });
				dispatch(setRolesByData(cloneState));
				break;
			}
			case 'edit': {
				const { Id } = selectedRoles[0];
				const isLocked: boolean = await lockedValidation(deviceObjectType, Id, false);
				if (isLocked) {
					return;
				}
				const params = new URLSearchParams(location.search);
				params.set('roleId', Id.toString());
				window.history.replaceState({}, '', `${location.pathname}?${params.toString()}`);
				handleOnRoleModalOpen(Id, false);
				break;
			}
		}
	};

	const changeStateProps = (id: number, userRole: Partial<UserRole>): UserRole[] => {
		const cloneState: UserRole[] = [...roles];
		const index = cloneState.findIndex(x => x.Id === id);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, ...userRole });
		}

		return cloneState;
	};

	const edit = (record: RolesColumn) => {
		form.setFieldsValue({
			Name: '',
			...record,
		});
		setEditingKey(record.key.toString());
	};

	const resetSearchColumn = () => {
		if (isFilterMode) {
			setShouldResetSearchColumn(true);
		}
	};

	const getSelectedRowKeysForSelectAll = async (newSelectedRowKeys: React.Key[]): Promise<React.Key[]> => {
		const response = await deviceAdminApi.getRoleIds();
		if (!handleResponse(response)) {
			const roleIds = response.Entity?.map<string>(x => x.toString());
			return getUniqueValuesArray(newSelectedRowKeys, roleIds);
		}
		return [];
	};

	const handleChange = (selectedRowKeys: React.Key[]) => {
		const cloneState: UserRole[] = roles.map(x => ({ ...x, checked: selectedRowKeys.findIndex(r => r === x.Id) !== -1 }));
		dispatch(setRolesByData(cloneState));
		setSelectedRowKeys(selectedRowKeys);
	};

	const handleSelectAll = async () => {
		setIsTableLoading(true);
		const newSelectedRowKeys = await getSelectedRowKeysForSelectAll(selectedRowKeys);
		const cloneState: UserRole[] = roles.map(x => ({ ...x, checked: newSelectedRowKeys.findIndex(r => r === x.Id?.toString()) !== -1 }));
		dispatch(setRolesByData(cloneState));
		setSelectedRowKeys(newSelectedRowKeys);
		setIsTableLoading(false);
	};

	const handleSelectInvert = () => {
		const dataKeys = roles.map<React.Key>(x => x.Id?.toString());
		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);
		const cloneState: UserRole[] = roles.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.Id?.toString()) !== -1,
		}));
		dispatch(setRolesByData(cloneState));
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const handleOnSaveEditRow = async (id: number) => {
		const isLocked: boolean = await lockedValidation(deviceObjectType, id, false);
		if (isLocked) {
			setEditingKey('');
			const cloneState: UserRole[] = changeStateProps(id, { editable: false });
			dispatch(setRolesByData(cloneState));
			dispatch(setVelocityConfigurationEditMode(false));
			return;
		}

		const name: string = form.getFieldValue('Name');
		const nameError: ResponseObject = await deviceAdminApi.renameDevice(deviceObjectType, name.trim(), id);
		if (nameError.ResponseStatusCode !== ResponseStatusCode.Success && nameError.ErrorMessage) {
			form.setFields([
				{
					name: 'Name',
					errors: [nameError.ErrorMessage],
				},
			]);
			return;
		} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
			notification['error']({
				message: nameError.ResponseErrorDescription,
			});
			setEditingKey('');
			dispatch(setVelocityConfigurationEditMode(false));
			dispatch(setRolesBy(tablePaginationSetting));
			return;
		}
		setEditingKey('');
		dispatch(setVelocityConfigurationEditMode(false));
		resetSearchColumn();
		setRedirectPage(true);
		dispatch(setRolesBy(tablePaginationSetting));
	};

	const handleOnCancelEditRow = (id: number) => {
		dispatch(setVelocityConfigurationEditMode(false));
		const cloneState: UserRole[] = changeStateProps(id, { editable: false });
		dispatch(setRolesByData(cloneState));
		setEditingKey('');
	};

	const createActionElement = (role: UserRole): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (role.editable) {
			const id = role.Id;
			content = (
				<>
					<Button id='renameSaveButton' key='save' type='primary' onClick={() => handleOnSaveEditRow(id)}>
						{_('Save')}
					</Button>
					<Button id='renameCancelButton' key='cancel' onClick={() => handleOnCancelEditRow(id)}>
						{_('Cancel')}
					</Button>
				</>
			);
		}
		return <div className={styles.actions}>{content}</div>;
	};

	const createData = (): RolesColumn[] => {
		if (roles.length > 0) {
			return roles.map<RolesColumn>((data: UserRole) => ({
				key: data.Id.toString(),
				Action: createActionElement(data),
				Name: data.Name,
				Description: data.Description,
			}));
		}

		return [];
	};

	const buttonsBuilder = (button: ButtonBuilder[]): React.ReactNode =>
		button.map(x => (
			<Button id={x.id} key={x.label} onClick={x.onClick} disabled={x.disabled} title={x.title} type='primary'>
				{x.icon} {x.label}
			</Button>
		));

	const buttons: ButtonBuilder[] = [
		{
			label: _('AddNewRole'),
			icon: <PlusOutlined />,
			disabled: !componentPermission.canAdd || isEditMode,
			onClick: () => {
				batch(() => {
					setIsAddModalOpen(true);
					setIsCopyMode(false);
				});
			},
			id: 'rolesAddButton',
			title: getPermissionErrorMessage(componentPermission.canAdd),
		},
	];

	const rowSelection: TableRowSelection<RolesColumn> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		getCheckboxProps: record => ({
			id: `rolesTableCheckbox-${record.key}`,
			disabled,
			children: <label htmlFor={`rolesTableCheckbox-${record.key}`} className={styles.srOnly}>{`${_('SelectRoles')}`}</label>,
		}),
		selectedRowKeys,
		onChange: handleChange,
	};

	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<RolesColumn>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});
		setSorterConfigEx({ columnKey: sorter.columnKey?.toString(), order: sorter.order });

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			dispatch(
				setRolesBy({
					...tablePaginationSetting,
					PageNumber: current,
					PageSize: pageSize,
					SortDirection: sortOrder,
					SortField: sortField.toString(),
				})
			);
		}
	};

	const disabledActionButtons = disabled || selectedRowKeys.length === 0;

	return (
		<div className={styles.container}>
			<div className={styles.buttonContainer}>
				<Space>
					<ButtonDropdown
						id={'rolesTableActionDropdown'}
						disabled={disabledActionButtons}
						menuOptions={items.options}
						icon={<CaretDownOutlined />}
						labelIcon={items.labelOrIcon}
						onClickOption={handleActionScope}
					/>
					{buttonsBuilder(buttons)}
				</Space>
				<HelpFactoryComponent helpFeature={HelpFeature.Roles} topicId={topicId} size='medium' />
			</div>
			<Form form={form}>
				<Table
					id='rolesTable'
					columns={mergedColumns as ColumnsType<RolesColumn>}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					dataSource={tableData}
					pagination={pagination}
					scroll={scroll}
					size='small'
					className={styles.rolesTable}
					rowSelection={rowSelection}
					onChange={handleOnChangeTable}
					loading={isTableLoading}
				/>
			</Form>
			{isAddModalOpen && (
				<AddRolesModal
					isCopyMode={isCopyMode}
					copyFromRoleId={isCopyMode ? (selectedRowKeys[0] as number) : undefined}
					onConfirm={() => {
						setIsAddModalOpen(false);
						setSelectedRowKeys([]);
						dispatch(setVelocitySelectedRowKeysPaginationAction([]));
						dispatch(setRolesBy(tablePaginationSetting));
					}}
					onCancel={() => setIsAddModalOpen(false)}
				/>
			)}
		</div>
	);
};

export { Roles };
